import React from 'react';
import { ArrowLeft, Check, Copy, Dog, Eye, Lock, LockOpen, Mail, MapPin, Plus, Search, Share2, Shuffle, Sunrise, Sunset, Trash2, UserPlus, X } from 'lucide-react';
import { api, type Mark, type MarkCalendarEvent, type MarkGroup, type MarkMember, type QidUser, type SatTemplate, type HuntInvitation, type SatOccupancy } from '../../api';
import { askForPush } from '../../push';
import { RADIUS, TYPE, useTheme } from '../../theme';
import {
  Badge, Button, Card, Chip, EmptyState, ErrorState, Field, Icon, IconButton, Input, Label, ListRow, Segmented, Sheet, Stepper, Switch, Textarea, useIsMobile,
} from '../../components/ui';
import { WMO_CODES } from '../../lib/season';
import { allRows, GAME_STATUS_LABEL, inviteRows, type HuntGameRow } from '../../lib/skjutmeny';
import { HuntInvitationView } from '../inbjudan/HuntInvitation';
import { PlanMap, type MapMarker, type MapPin as MarkPin, type MapSat } from './PlanMap';
import { busyBySat, occupancyLabel, parseSatar, pointInSat, satColor } from '../../karta/satar';
import {
  MARKER_LABEL, STEPS, UTRUSTNING, applyTemplate, assignPost, clearDraft, draftFromEvent, drawPosts, emptyDraft, loadDraft, parseGuest,
  passLabelFor, postsOf, pruneAssignments, recipientsOf, saveDraft, setPostLocked, templateJson, toIso, toPlanHuntInput, toggleMarkSat, uid, validateAll, validateStep,
  type Errors, type PlanDraft, type PlanMember, type PlanSat, type StepKey,
} from './planModel';

// Planera jakt (J1): fem steg – När · Plats och drev · Vilka · Pass · Skicka.
// Mobil: ett steg i taget med fast bottenrad. Desktop: stegen till vänster och
// inbjudan som förhandsvisning till höger. Utkastet sparas i localStorage per mark.

type Props = {
  mark: Mark;
  members: MarkMember[];
  user: QidUser | null | undefined;
  /** Finns = ändra en planerad jakt. */
  editEvent?: MarkCalendarEvent | null;
  onDone: (ev: MarkCalendarEvent) => void;
  onClose: () => void;
};

type MapMode = 'samling' | 'pass' | 'hund_start';

export default function PlaneraJakt({ mark, members, user, editEvent, onDone, onClose }: Props) {
  const T = useTheme();
  const mob = useIsMobile();
  const planMembers: PlanMember[] = React.useMemo(() => members.map(m => ({ id: m.id, name: m.name, userEmail: m.userEmail, phone: m.phone, dogHandler: m.dogHandler, role: m.role })), [members]);
  const userName = user?.name || [user?.given_name, user?.family_name].filter(Boolean).join(' ') || user?.email?.split('@')[0] || null;
  const me = React.useMemo(() => planMembers.find(m => user?.email && m.userEmail?.toLowerCase() === user.email.toLowerCase()) ?? null, [planMembers, user?.email]);

  const [draft, setDraftRaw] = React.useState<PlanDraft>(() => {
    if (editEvent) return draftFromEvent(editEvent, planMembers);
    return loadDraft(mark.id) ?? emptyDraft({ markName: mark.name, me, userName });
  });
  const restored = React.useRef(!editEvent && !!loadDraft(mark.id));
  const setDraft = React.useCallback((fn: (d: PlanDraft) => PlanDraft) => setDraftRaw(d => fn(d)), []);
  React.useEffect(() => { if (!editEvent) saveDraft(mark.id, draft); }, [draft, editEvent, mark.id]);

  const [step, setStep] = React.useState(0);
  const [visited, setVisited] = React.useState(0);
  const [errors, setErrors] = React.useState<Errors>({});
  const [busy, setBusy] = React.useState(false);
  const [sendErr, setSendErr] = React.useState<string | null>(null);
  const [notify, setNotify] = React.useState(true);
  const [showPreview, setShowPreview] = React.useState(false);
  const [result, setResult] = React.useState<MarkCalendarEvent | null>(null);

  const stepKey = STEPS[step].key as StepKey;
  const go = (i: number) => { setErrors({}); setStep(i); setVisited(v => Math.max(v, i)); document.getElementById('planera-body')?.scrollTo({ top: 0 }); };
  const next = () => {
    const e = validateStep(stepKey, draft, me?.id);
    if (Object.keys(e).length) { setErrors(e); return; }
    go(Math.min(step + 1, STEPS.length - 1));
  };

  const send = async () => {
    const bad = validateAll(draft, me?.id);
    if (bad) { setStep(STEPS.findIndex(s => s.key === bad.step)); setErrors(bad.errors); return; }
    setBusy(true); setSendErr(null);
    try {
      const input = { ...toPlanHuntInput(pruneAssignments(draft, planMembers), planMembers), gameRowsJson: JSON.stringify(gameRows) };
      const ev = editEvent
        ? await api.updatePlannedHunt(editEvent.id, input, notify)
        : await api.planHunt(mark.id, input, draft.sendEmail);
      if (!editEvent) clearDraft(mark.id);
      void askForPush(); // svaren på inbjudan kommer som push
      setResult(ev);
      onDone(ev);
    } catch (e) {
      setSendErr((e as Error).message || 'Jakten kunde inte sparas');
    }
    setBusy(false);
  };

  const recipients = recipientsOf(draft, planMembers);
  const withEmail = recipients.filter(r => r.email).length;
  // Skjutmenyn för jaktdagen från API:t (samma regler som kartans skjutmeny, hela dagen).
  // API:t räknar om den när jakten sparas; det här är förhandsvisningen.
  const huntIso = toIso(draft.date, draft.startTime) ?? `${draft.date}T12:00:00`;
  const [gameRows, setGameRows] = React.useState<HuntGameRow[]>([]);
  // Planerarens egna fällavgifter (för förhandsvisningen) och dagens sol upp/ned från samma svar.
  const [gameFees, setGameFees] = React.useState<Record<string, { fee: number | null; feeFrom: boolean }>>({});
  const [daySun, setDaySun] = React.useState<{ sunrise: string; sunset: string } | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    const at = new Date(huntIso);
    if (Number.isNaN(at.getTime())) return;
    api.markSkjutmeny(mark.id, { at: at.toISOString(), wholeDay: true })
      .then(m => {
        if (cancelled) return;
        setGameRows(inviteRows(allRows(m)));
        setGameFees(Object.fromEntries(allRows(m).map(r => [r.gameType, { fee: r.fieldFee, feeFrom: r.feeFrom }])));
        const hm = (iso: string) => new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' });
        setDaySun(m.sunrise && m.sunset ? { sunrise: hm(m.sunrise), sunset: hm(m.sunset) } : null);
      })
      .catch(() => { if (!cancelled) { setGameRows([]); setDaySun(null); } });
    return () => { cancelled = true; };
  }, [mark.id, huntIso]);
  const preview = React.useMemo(() => previewInvitation(draft, planMembers, mark, me, userName, gameRows, gameFees), [draft, planMembers, mark, me, userName, gameRows, gameFees]);

  if (result) return <Done ev={result} edited={!!editEvent} draft={draft} mark={mark} onClose={onClose} />;

  const stepBody = (
    <>
      {stepKey === 'nar' && <StepNar draft={draft} setDraft={setDraft} errors={errors} mark={mark} gameRows={gameRows} sun={daySun} restored={restored.current && step === 0} onReset={() => { restored.current = false; setDraftRaw(emptyDraft({ markName: mark.name, me, userName })); }} />}
      {stepKey === 'plats' && <StepPlats draft={draft} setDraft={setDraft} errors={errors} mark={mark} editKey={editEvent ? `hunt:${editEvent.id}` : null} />}
      {stepKey === 'vilka' && <StepVilka draft={draft} setDraft={setDraft} errors={errors} mark={mark} members={planMembers} meId={me?.id ?? null} />}
      {stepKey === 'pass' && <StepPass draft={draft} setDraft={setDraft} members={planMembers} mark={mark} onGoMap={() => go(1)} />}
      {stepKey === 'skicka' && <StepSkicka draft={draft} setDraft={setDraft} mark={mark} edit={!!editEvent} notify={notify} setNotify={setNotify}
        recipients={recipients.length} withEmail={withEmail} onPreview={mob ? () => setShowPreview(true) : undefined} />}
      {sendErr && <div style={{ marginTop: 16 }}><ErrorState title="Inbjudan skickades inte" message={sendErr} /></div>}
    </>
  );

  const isLast = step === STEPS.length - 1;
  const primary = isLast
    ? <Button onClick={send} loading={busy} block={mob} size={mob ? 'lg' : 'md'} icon={<Icon as={editEvent ? Check : Mail} size={18} />}>{editEvent ? 'Spara ändringar' : 'Skicka inbjudan'}</Button>
    : <Button onClick={next} block={mob} size={mob ? 'lg' : 'md'}>Nästa: {STEPS[step + 1].label.toLowerCase()}</Button>;
  const back = step > 0 && <Button variant="secondary" onClick={() => go(step - 1)} size={mob ? 'lg' : 'md'} style={{ flex: '0 0 auto' }}>Tillbaka</Button>;

  return (
    <div role="dialog" aria-modal="true" aria-label={editEvent ? 'Ändra jakt' : 'Planera jakt'} data-testid="planera-jakt"
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: T.bg, color: T.text, display: 'flex', flexDirection: 'column' }}>
      {/* Huvud */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 56, padding: '0 8px 0 4px', paddingTop: 'env(safe-area-inset-top, 0px)', background: T.surface, borderBottom: `1px solid ${T.border}` }}>
        <IconButton label="Stäng" onClick={onClose}><X size={22} strokeWidth={2} /></IconButton>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...TYPE.title, lineHeight: 1.2 }}>{editEvent ? 'Ändra jakt' : 'Planera jakt'}</div>
          <div style={{ ...TYPE.small, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mark.name}</div>
        </div>
        {mob ? <span style={{ ...TYPE.small, color: T.textMuted, paddingRight: 8 }}>{step + 1} av {STEPS.length}</span>
          : <div style={{ display: 'flex', gap: 8 }}>{back}{primary}</div>}
      </div>
      {mob && <div style={{ padding: '10px 16px 0' }}><Stepper steps={STEPS} current={step} done={i => i < step} /></div>}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {!mob && (
          <aside style={{ width: 200, flexShrink: 0, padding: 16, borderRight: `1px solid ${T.border}` }}>
            <Stepper vertical steps={STEPS} current={step} done={i => i <= visited && Object.keys(validateStep(STEPS[i].key, draft, me?.id)).length === 0} onSelect={go} />
          </aside>
        )}
        <main id="planera-body" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: mob ? '16px 16px 24px' : '24px 32px 48px' }}>
          <div style={{ maxWidth: 640, margin: '0 auto' }}>{stepBody}</div>
        </main>
        {!mob && (
          <aside aria-label="Förhandsvisning av inbjudan" style={{ width: 380, flexShrink: 0, overflowY: 'auto', padding: 16, background: T.surface2, borderLeft: `1px solid ${T.border}` }}>
            <Label>Förhandsvisning</Label>
            <div style={{ marginTop: 8, background: T.bg, borderRadius: RADIUS.lg, padding: 12 }}>
              <HuntInvitationView inv={preview} preview compact />
            </div>
          </aside>
        )}
      </div>

      {mob && (
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: T.surface, borderTop: `1px solid ${T.border}` }}>
          {back}{primary}
        </div>
      )}

      <Sheet open={showPreview} onClose={() => setShowPreview(false)} title="Så ser inbjudan ut">
        <div style={{ padding: '0 16px 16px' }}><HuntInvitationView inv={preview} preview /></div>
      </Sheet>
    </div>
  );
}

// ─── Steg 1: När ──────────────────────────────────────────────────

function StepNar({ draft, setDraft, errors, mark, gameRows, sun, restored, onReset }: { draft: PlanDraft; setDraft: (fn: (d: PlanDraft) => PlanDraft) => void; errors: Errors; mark: Mark; gameRows: HuntGameRow[]; sun: { sunrise: string; sunset: string } | null; restored: boolean; onReset: () => void }) {
  const T = useTheme();
  const [templates, setTemplates] = React.useState<SatTemplate[]>([]);
  const [tplMsg, setTplMsg] = React.useState('');
  React.useEffect(() => { api.markSatTemplates(mark.id).then(setTemplates).catch(() => {}); }, [mark.id]);
  const date = draft.date ? new Date(`${draft.date}T12:00:00`) : null;
  const lat = mark.mapLat ?? null, lng = mark.mapLng ?? null;
  const weather = useWeather(lat, lng, draft.date);
  const set = (k: keyof PlanDraft) => (e: React.ChangeEvent<HTMLInputElement>) => { const v = e.target.value; setDraft(d => ({ ...d, [k]: v })); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepTitle title="När är jakten?" sub="Datum och tider. Resten kan du fylla i efter hand." />
      {restored && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: RADIUS.md, background: T.infoSoft, color: T.info, ...TYPE.small }}>
          <span style={{ flex: 1 }}>Vi har sparat ditt utkast från förra gången.</span>
          <Button size="sm" variant="ghost" onClick={onReset}>Börja om</Button>
        </div>
      )}
      <Field label="Rubrik" error={errors.title}><Input value={draft.title} onChange={set('title')} placeholder="Älgjakt på Esta" /></Field>
      <Field label="Datum" error={errors.date}><Input type="date" value={draft.date} onChange={set('date')} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
        <Field label="Samling" error={errors.meetingTime}><Input type="time" value={draft.meetingTime} onChange={set('meetingTime')} /></Field>
        <Field label="Jaktstart" error={errors.startTime}><Input type="time" value={draft.startTime} onChange={set('startTime')} /></Field>
        <Field label="Slut" hint={!errors.endTime ? 'Valfritt' : undefined} error={errors.endTime}><Input type="time" value={draft.endTime} onChange={set('endTime')} /></Field>
      </div>

      {date && (
        <Card padding={14}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', ...TYPE.small, color: T.textSub }}>
            {sun && <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Icon as={Sunrise} size={18} />Sol upp {sun.sunrise}</span>}
            {sun && <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Icon as={Sunset} size={18} />Sol ned {sun.sunset}</span>}
            {weather && <span>{weather}</span>}
          </div>
          <div style={{ marginTop: 10 }}>
            <Label>Skjutmeny den dagen</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {gameRows.length ? gameRows.map(g => (
                <Badge key={g.gameType} tone={g.status === 'lovligt' ? 'ok' : g.status === 'slut' ? 'danger' : 'neutral'}>
                  {g.gameType}{g.remaining != null ? ` · ${g.remaining} kvar` : ''}{g.status !== 'lovligt' ? ` · ${GAME_STATUS_LABEL[g.status].toLowerCase()}` : ''}
                </Badge>
              )) : <span style={{ ...TYPE.small, color: T.textMuted }}>Inget vilt är lovligt det datumet.</span>}
            </div>
          </div>
        </Card>
      )}

      {templates.length > 0 && (
        <div>
          <Label>Börja från en sparad plan</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {templates.map(t => (
              <Chip key={t.id} onClick={() => {
                try { setDraft(d => applyTemplate(d, t.planJson)); setTplMsg(`"${t.name}" är inläst: såtar, pass, utrustning och meddelande.`); } catch { setTplMsg('Planen kunde inte läsas'); }
              }}>{t.name}</Chip>
            ))}
          </div>
          {tplMsg && <div role="status" style={{ ...TYPE.small, color: T.ok, marginTop: 6 }}>{tplMsg}</div>}
        </div>
      )}
    </div>
  );
}

function useWeather(lat: number | null, lng: number | null, date: string): string | null {
  const [w, setW] = React.useState<string | null>(null);
  React.useEffect(() => {
    setW(null);
    if (lat == null || lng == null || !date) return;
    const days = (new Date(`${date}T12:00:00`).getTime() - Date.now()) / 86400000;
    if (days < -1 || days > 15) return; // Open-Meteo ger prognos 16 dagar framåt
    let off = false;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,windspeed_10m_max,weathercode&timezone=Europe/Stockholm&start_date=${date}&end_date=${date}`)
      .then(r => r.json())
      .then(d => {
        const x = d?.daily; if (off || !x?.temperature_2m_max?.length) return;
        setW(`${WMO_CODES[x.weathercode[0]] ?? 'Väder'} · ${Math.round(x.temperature_2m_min[0])}–${Math.round(x.temperature_2m_max[0])}° · vind ${Math.round(x.windspeed_10m_max[0] / 3.6)} m/s`);
      })
      .catch(() => {});
    return () => { off = true; };
  }, [lat, lng, date]);
  return w;
}

// ─── Steg 2: Plats och drev ───────────────────────────────────────

function StepPlats({ draft, setDraft, errors, mark, editKey }: { draft: PlanDraft; setDraft: (fn: (d: PlanDraft) => PlanDraft) => void; errors: Errors; mark: Mark; editKey: string | null }) {
  const T = useTheme();
  const [mode, setMode] = React.useState<MapMode>(draft.meetingLat == null ? 'samling' : 'pass');
  const [active, setActive] = React.useState(0);
  const [pins, setPins] = React.useState<MarkPin[]>([]);
  React.useEffect(() => { api.markPins(mark.id).then(ps => setPins(ps.map((p: any) => ({ id: p.id, lat: Number(p.lat), lng: Number(p.lng), type: p.type, name: p.name })))).catch(() => {}); }, [mark.id]);

  // S1: markens såter och vad som redan är bokat den dagen (andra jakter, köpta annonser).
  const markSatar = React.useMemo(() => parseSatar((mark as any).coordinatesJson), [(mark as any).coordinatesJson]);
  const [occ, setOcc] = React.useState<SatOccupancy[]>([]);
  React.useEffect(() => {
    if (!markSatar.length || !draft.date) { setOcc([]); return; }
    let off = false;
    api.markSatOccupancy(mark.id, draft.date).then(o => { if (!off) setOcc(o); }).catch(() => { if (!off) setOcc([]); });
    return () => { off = true; };
  }, [mark.id, draft.date, markSatar.length]);
  const busy = React.useMemo(() => busyBySat(markSatar, occ, editKey), [markSatar, occ, editKey]);
  const chosen = new Set(draft.satar.map(s => s.markSatId).filter(Boolean) as string[]);
  const pickSat = (id: string) => {
    const s = markSatar.find(x => x.id === id);
    if (!s) return;
    if (!chosen.has(id) && busy.get(id)?.length) return; // upptagen: kan inte väljas
    setDraft(d => {
      const next = toggleMarkSat(d, { id: s.id, name: s.name, contains: (lat, lng) => pointInSat(lat, lng, s.geometry) }, pins);
      const idx = next.satar.findIndex(x => x.markSatId === id);
      if (idx >= 0) setActive(idx); else setActive(0);
      return next;
    });
    if (mode === 'samling' && draft.meetingLat != null) setMode('pass');
  };
  const mapSatar: MapSat[] = markSatar.map((s, i) => ({
    id: s.id, name: s.name, geometry: s.geometry, color: satColor(i), selected: chosen.has(s.id),
    busy: busy.get(s.id)?.length ? occupancyLabel(busy.get(s.id)![0]) : null,
  }));
  const otherSameDay = occ.filter(o => o.key !== editKey);

  const sat = draft.satar[active] as PlanSat | undefined;
  const ensureSat = (d: PlanDraft): [PlanDraft, number] => {
    if (d.satar[active]) return [d, active];
    const s: PlanSat = { id: uid(), namn: `Såt ${d.satar.length + 1}`, markers: [] };
    return [{ ...d, satar: [...d.satar, s] }, d.satar.length];
  };
  const addMarker = (lat: number, lng: number, label = '') => setDraft(d0 => {
    const [d, i] = ensureSat(d0);
    if (i !== active) setActive(i);
    const type = mode === 'hund_start' ? 'hund_start' : 'pass';
    return { ...d, satar: d.satar.map((s, j) => j === i ? { ...s, markers: [...s.markers, { id: uid(), type, lat, lng, label }] } : s) };
  });
  const updSat = (i: number, fn: (s: PlanSat) => PlanSat) => setDraft(d => ({ ...d, satar: d.satar.map((s, j) => j === i ? fn(s) : s) }));

  const markers: MapMarker[] = draft.satar.flatMap((s, i) => s.markers.map(m => ({ id: m.id, type: m.type, lat: m.lat, lng: m.lng, label: m.label || MARKER_LABEL[m.type], assignedName: m.assignedName, dim: i !== active })));
  const meeting = draft.meetingLat != null && draft.meetingLng != null ? { lat: draft.meetingLat, lng: draft.meetingLng, label: draft.meetingPlace || 'Samlingsplats' } : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepTitle title="Var ses ni, och hur jagas det?" sub="Samlingsplatsen behövs. Såtar och pass kan du lägga ut nu eller senare." />
      <Segmented label="Vad du lägger ut på kartan" value={mode} onChange={v => setMode(v as MapMode)} block
        options={[{ value: 'samling', label: 'Samlingsplats' }, { value: 'pass', label: 'Pass' }, { value: 'hund_start', label: 'Hundförare' }]} />
      <div>
        <PlanMap
          height={340} centerLat={mark.mapLat} centerLng={mark.mapLng} boundaryGeoJson={(mark as any).coordinatesJson ?? null}
          pins={pins} markers={markers} meeting={meeting} ariaLabel="Karta för samlingsplats och pass"
          satar={markSatar.length ? mapSatar : undefined}
          onMapClick={(lat, lng) => mode === 'samling' ? setDraft(d => ({ ...d, meetingLat: lat, meetingLng: lng })) : addMarker(lat, lng)}
          onPinClick={p => mode === 'samling'
            ? setDraft(d => ({ ...d, meetingLat: p.lat, meetingLng: p.lng, meetingPlace: d.meetingPlace || p.name || '' }))
            : addMarker(p.lat, p.lng, p.name || '')}
        />
        <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 6 }}>
          {mode === 'samling' ? 'Tryck på kartan eller på en av markens nålar där ni samlas.' : `Tryck på kartan (eller en befintlig nål) för att lägga ut ${mode === 'pass' ? 'ett pass' : 'en hundförarpost'} i ${sat?.namn ?? 'en ny såt'}.`}
        </div>
      </div>

      {markSatar.length > 0 && (
        <div data-testid="markens-sater">
          <Label>Markens såter</Label>
          <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>Välj vilka såter jakten gäller. Markens pass i såten läggs ut. Bokade såter går inte att välja.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {markSatar.map((s, i) => {
              const b = busy.get(s.id) ?? [];
              const on = chosen.has(s.id);
              return (
                <Chip key={s.id} selected={on} disabled={!on && b.length > 0} onClick={() => pickSat(s.id)}
                  icon={<span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: b.length && !on ? '#e03131' : satColor(i), display: 'inline-block' }} />}>
                  {s.name}{b.length && !on ? ' · bokad' : ''}
                </Chip>
              );
            })}
          </div>
          {otherSameDay.length > 0 && (
            <div role="status" style={{ marginTop: 8, padding: '8px 12px', borderRadius: RADIUS.md, background: T.amberDim, color: T.text, ...TYPE.small }}>
              <strong>Samma dag på marken:</strong>{' '}
              {otherSameDay.map(o => `${occupancyLabel(o)}${o.wholeMark ? '' : ` (${o.satIds.map(id => markSatar.find(x => x.id === id)?.name).filter(Boolean).join(', ')})`}`).join(' · ')}
            </div>
          )}
          {chosen.size === 0 && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 6 }}>Ingen såt vald = jakten gäller hela marken.</div>}
        </div>
      )}

      <Field label="Samlingsplats" error={errors.meetingPlace} hint={meeting ? 'Nålen syns i inbjudan med vägbeskrivning.' : undefined}>
        <Input value={draft.meetingPlace} placeholder="Vägbommen vid ladan" onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, meetingPlace: v })); }} />
      </Field>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Label>Såtar</Label>
          <span style={{ flex: 1 }} />
          <Button size="sm" variant="secondary" icon={<Icon as={Plus} size={16} />} onClick={() => { setDraft(d => ({ ...d, satar: [...d.satar, { id: uid(), namn: `Såt ${d.satar.length + 1}`, markers: [] }] })); setActive(draft.satar.length); if (mode === 'samling') setMode('pass'); }}>Ny såt</Button>
        </div>
        {draft.satar.length === 0 ? (
          <div style={{ ...TYPE.small, color: T.textMuted }}>Inga såtar ännu. Välj Pass ovan och tryck på kartan, så skapas Såt 1.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {draft.satar.map((s, i) => <Chip key={s.id} selected={i === active} onClick={() => setActive(i)} count={s.markers.length}>{s.namn}</Chip>)}
            </div>
            {sat && (
              <Card padding={14} style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <Field label="Namn på såten" style={{ flex: 1 }}><Input value={sat.namn} onChange={e => { const v = e.target.value; updSat(active, s => ({ ...s, namn: v })); }} /></Field>
                  <IconButton label={`Ta bort ${sat.namn}`} onClick={() => { setDraft(d => ({ ...d, satar: d.satar.filter((_, j) => j !== active) })); setActive(0); }}><Trash2 size={18} strokeWidth={2} color={T.danger} /></IconButton>
                </div>
                {sat.markers.length === 0 ? <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 10 }}>Tryck på kartan för att lägga ut pass.</div> : (
                  <div style={{ marginTop: 10 }}>
                    {sat.markers.map((m, mi) => { const nr = sat.markers.slice(0, mi + 1).filter(x => x.type === m.type).length; return (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 48, borderTop: mi ? `1px solid ${T.border}` : 'none' }}>
                        <span aria-hidden style={{ width: 12, height: 12, borderRadius: '50%', background: m.type === 'hund_start' ? '#c96a0c' : '#2f7d32', flexShrink: 0 }} />
                        <Input aria-label={`Namn på ${MARKER_LABEL[m.type].toLowerCase()} ${nr}`} value={m.label} placeholder={`${MARKER_LABEL[m.type]} ${nr}`}
                          onChange={e => { const v = e.target.value; updSat(active, s => ({ ...s, markers: s.markers.map(x => x.id === m.id ? { ...x, label: v } : x) })); }} style={{ flex: 1 }} />
                        <IconButton label="Ta bort" onClick={() => updSat(active, s => ({ ...s, markers: s.markers.filter(x => x.id !== m.id) }))}><X size={18} strokeWidth={2} /></IconButton>
                      </div>
                    ); })}
                  </div>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Steg 3: Vilka ────────────────────────────────────────────────

function StepVilka({ draft, setDraft, errors, mark, members, meId }: { draft: PlanDraft; setDraft: (fn: (d: PlanDraft) => PlanDraft) => void; errors: Errors; mark: Mark; members: PlanMember[]; meId: string | null }) {
  const T = useTheme();
  const [groups, setGroups] = React.useState<MarkGroup[]>([]);
  const [q, setQ] = React.useState('');
  const [contact, setContact] = React.useState('');
  const [gname, setGname] = React.useState('');
  const [gerr, setGerr] = React.useState<string | null>(null);
  React.useEffect(() => { api.markGroups(mark.id).then(setGroups).catch(() => {}); }, [mark.id]);

  const sel = new Set(draft.memberIds);
  const toggle = (id: string) => setDraft(d => ({ ...d, memberIds: sel.has(id) && id !== meId ? d.memberIds.filter(x => x !== id) : sel.has(id) ? d.memberIds : [...d.memberIds, id] }));
  const setMany = (ids: string[], on: boolean) => setDraft(d => ({ ...d, memberIds: on ? [...new Set([...d.memberIds, ...ids])] : d.memberIds.filter(x => !ids.includes(x) || x === meId) }));
  const valid = (ids: string[]) => ids.filter(id => members.some(m => m.id === id));
  const shown = q ? members.filter(m => m.name.toLowerCase().includes(q.toLowerCase())) : members;
  const allIds = members.map(m => m.id);
  const allOn = allIds.length > 0 && allIds.every(id => sel.has(id));

  const addGuest = () => {
    const g = parseGuest(contact, gname);
    if ('error' in g) { setGerr(g.error); return; }
    if (draft.guests.some(x => x.key === g.key)) { setGerr('Den gästen är redan inbjuden'); return; }
    if (g.email && members.some(m => m.userEmail?.toLowerCase() === g.email)) { setGerr('Det är en medlem – välj hen i listan ovan'); return; }
    setDraft(d => ({ ...d, guests: [...d.guests, g] }));
    setContact(''); setGname(''); setGerr(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepTitle title="Vilka bjuds in?" sub={`${draft.memberIds.length + draft.guests.length} valda. Alla med e-post får inbjudan i ett mejl.`} />
      {errors.recipients && <div role="alert" style={{ color: T.danger, ...TYPE.small, fontWeight: 600 }}>{errors.recipients}</div>}

      <div>
        <Label>Snabbval</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <Chip selected={allOn} onClick={() => setMany(allIds, !allOn)} count={allIds.length}>Alla medlemmar</Chip>
          {groups.map(g => {
            const ids = valid(g.memberIds ?? []);
            if (!ids.length) return null;
            const on = ids.every(id => sel.has(id));
            return <Chip key={g.id} selected={on} onClick={() => setMany(ids, !on)} count={ids.length}>{g.name}</Chip>;
          })}
          {!groups.some(g => g.name.trim().toLowerCase() === 'hundförare') && members.some(m => m.dogHandler) && (
            <Chip selected={false} icon={<Dog size={16} strokeWidth={2} aria-hidden />} onClick={() => setMany(members.filter(m => m.dogHandler).map(m => m.id), true)} count={members.filter(m => m.dogHandler).length}>Hundförare</Chip>
          )}
        </div>
      </div>

      <div>
        <Label>Medlemmar</Label>
        {members.length > 8 && (
          <div style={{ position: 'relative', marginTop: 8 }}>
            <span style={{ position: 'absolute', left: 12, top: 12, color: T.textMuted }}><Icon as={Search} size={20} /></span>
            <Input aria-label="Sök medlem" placeholder="Sök medlem" value={q} onChange={e => setQ(e.target.value)} style={{ paddingLeft: 40 }} />
          </div>
        )}
        <Card padding={0} style={{ marginTop: 8 }}>
          {shown.length === 0 && <div style={{ padding: 16, ...TYPE.small, color: T.textMuted }}>Inga medlemmar hittades.</div>}
          {shown.map((m, i) => {
            const on = sel.has(m.id);
            const sub = [m.id === meId ? 'du · jaktledare' : null, m.dogHandler ? 'hundförare' : null, !m.userEmail ? (m.phone ? 'ingen e-post – får länk' : 'ingen e-post') : null].filter(Boolean).join(' · ');
            return (
              <ListRow key={m.id} divider={i < shown.length - 1} onClick={() => toggle(m.id)}
                icon={<Avatar name={m.name} on={on} />}
                title={m.name} sub={sub || undefined}
                trailing={<span role="checkbox" aria-checked={on} aria-label={m.name} style={{ width: 24, height: 24, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: on ? T.accentFill : 'transparent', color: T.onAccent, border: on ? 'none' : `2px solid ${T.borderHover}` }}>{on && <Check size={16} strokeWidth={3} />}</span>} />
            );
          })}
        </Card>
      </div>

      <div>
        <Label>Gäster utan konto</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <Input aria-label="Gästens e-post eller telefon" placeholder="namn@exempel.se eller 070-123 45 67" value={contact} onChange={e => { setContact(e.target.value); setGerr(null); }}
            onKeyDown={e => { if (e.key === 'Enter') addGuest(); }} style={{ flex: '2 1 220px' }} />
          <Input aria-label="Gästens namn" placeholder="Namn" value={gname} onChange={e => setGname(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addGuest(); }} style={{ flex: '1 1 140px' }} />
          <Button variant="secondary" icon={<Icon as={UserPlus} size={18} />} onClick={addGuest}>Lägg till</Button>
        </div>
        {gerr && <div role="alert" style={{ color: T.danger, ...TYPE.small, fontWeight: 600, marginTop: 6 }}>{gerr}</div>}
        {draft.guests.length > 0 && (
          <Card padding={0} style={{ marginTop: 8 }}>
            {draft.guests.map((g, i) => (
              <ListRow key={g.key} divider={i < draft.guests.length - 1} icon={<Icon as={g.email ? Mail : Share2} />}
                title={g.name} sub={g.email ? `${g.email} · får mejl` : `${g.phone} · får en länk att dela`}
                trailing={<IconButton label={`Ta bort ${g.name}`} onClick={() => setDraft(d => ({ ...d, guests: d.guests.filter(x => x.key !== g.key) }))}><X size={18} strokeWidth={2} /></IconButton>} />
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

function Avatar({ name, on }: { name: string; on?: boolean }) {
  const T = useTheme();
  const ini = name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return <span style={{ width: 36, height: 36, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, background: on ? T.accentSoft : T.surface2, color: on ? T.accentText : T.textSub }}>{ini}</span>;
}

// ─── Steg 4: Pass ─────────────────────────────────────────────────

function StepPass({ draft, setDraft, members, mark, onGoMap }: { draft: PlanDraft; setDraft: (fn: (d: PlanDraft) => PlanDraft) => void; members: PlanMember[]; mark: Mark; onGoMap: () => void }) {
  const T = useTheme();
  const [msg, setMsg] = React.useState<string | null>(null);
  const posts = postsOf(draft);
  const people = recipientsOf(draft, members);
  React.useEffect(() => { setDraft(d => pruneAssignments(d, members)); }, [members, setDraft]);

  if (posts.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <StepTitle title="Pass" sub="Valfritt. Du kan lotta passen senare, även samma morgon." />
        <Card><EmptyState icon={MapPin} title="Inga pass på kartan" sub="Lägg ut pass och hundförarposter under Plats, så kan du lotta eller tilldela dem här."
          action={<Button variant="secondary" onClick={onGoMap}>Lägg ut pass</Button>} /></Card>
      </div>
    );
  }

  const markers: MapMarker[] = draft.satar.flatMap(s => s.markers.map(m => ({ id: m.id, type: m.type, lat: m.lat, lng: m.lng, label: m.label || MARKER_LABEL[m.type], assignedName: m.assignedName })));
  const assigned = posts.filter(p => p.marker.assigneeKey).length;
  const locked = posts.filter(p => p.marker.locked).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepTitle title="Vem står var?" sub={`${posts.length} poster · ${people.length} inbjudna · ${assigned} tilldelade. Valfritt – kan göras senare.`} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button icon={<Icon as={Shuffle} size={18} />} onClick={() => {
          const r = drawPosts(draft, members);
          setDraft(() => r.draft);
          const parts = [locked ? `Passen är lottade (${locked} ${locked === 1 ? 'låst pass ingick' : 'låsta pass ingick'} inte).` : 'Passen är lottade.'];
          if (r.unassigned.length) parts.push(`Utan pass: ${r.unassigned.join(', ')}.`);
          if (r.emptyPosts) parts.push(`${r.emptyPosts} ${r.emptyPosts === 1 ? 'post är' : 'poster är'} tomma.`);
          setMsg(parts.join(' '));
        }}>{assigned ? 'Lotta om' : 'Lotta pass'}</Button>
        {assigned > 0 && <Button variant="ghost" onClick={() => { setDraft(d => ({ ...d, satar: d.satar.map(s => ({ ...s, markers: s.markers.map(m => m.locked ? m : ({ ...m, assigneeKey: null, assignedName: undefined })) })) })); setMsg(null); }}>Rensa</Button>}
      </div>
      {msg && <div role="status" style={{ ...TYPE.small, color: T.textSub }}>{msg}</div>}
      <div style={{ ...TYPE.small, color: T.textMuted }}>Lås ett pass (hänglåset) för t.ex. jaktledare och hundförare – låsta pass ingår inte när du lottar.</div>
      <PlanMap height={240} centerLat={mark.mapLat} centerLng={mark.mapLng} markers={markers} ariaLabel="Karta med passen" />
      <Card padding={0}>
        {posts.map((p, i) => (
          <div key={p.marker.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', minHeight: 56, borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <span aria-hidden style={{ width: 12, height: 12, borderRadius: '50%', background: p.marker.type === 'hund_start' ? '#c96a0c' : '#2f7d32', flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, ...TYPE.body }}>{p.label}</span>
            <IconButton label={p.marker.locked ? `Lås upp ${p.label}` : `Lås ${p.label}`} aria-pressed={!!p.marker.locked}
              onClick={() => setDraft(d => setPostLocked(d, p.marker.id, !p.marker.locked))}>
              <Icon as={p.marker.locked ? Lock : LockOpen} size={18} color={p.marker.locked ? T.amber : T.textMuted} />
            </IconButton>
            <select aria-label={`Vem står på ${p.label}`} value={p.marker.assigneeKey ?? ''} onChange={e => setDraft(d => assignPost(d, p.marker.id, e.target.value || null, members))}
              style={{ minHeight: 44, maxWidth: 200, borderRadius: RADIUS.md, border: `1px solid ${T.borderHover}`, background: T.surface, color: T.text, fontSize: 16, padding: '0 10px', fontFamily: 'inherit' }}>
              <option value="">Ingen</option>
              {people.map(r => <option key={r.key} value={r.key}>{r.name}{r.dogHandler ? ' (hund)' : ''}</option>)}
            </select>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── Steg 5: Granska och skicka ───────────────────────────────────

function StepSkicka({ draft, setDraft, mark, edit, notify, setNotify, recipients, withEmail, onPreview }: {
  draft: PlanDraft; setDraft: (fn: (d: PlanDraft) => PlanDraft) => void; mark: Mark; edit: boolean; notify: boolean; setNotify: (v: boolean) => void;
  recipients: number; withEmail: number; onPreview?: () => void;
}) {
  const T = useTheme();
  const [tplName, setTplName] = React.useState<string | null>(null);
  const [tplMsg, setTplMsg] = React.useState('');
  const toggleGear = (g: string) => setDraft(d => ({ ...d, utrustning: d.utrustning.includes(g) ? d.utrustning.filter(x => x !== g) : [...d.utrustning, g] }));
  const withoutEmail = recipients - withEmail;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepTitle title={edit ? 'Granska och spara' : 'Granska och skicka'} sub="Lägg till det deltagarna behöver veta." />
      {onPreview && <Button variant="secondary" icon={<Icon as={Eye} size={18} />} onClick={onPreview}>Förhandsgranska inbjudan</Button>}
      <Field label="Meddelande till deltagarna" hint="Säkerhet, klädsel, skottriktningar, lunch – syns överst i inbjudan och mejlet.">
        <Textarea value={draft.message} rows={4} placeholder={'Orange väst och mössa. Skott endast mot säker bakgrund.\nLunch vid stugan kl 12.'} onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, message: v })); }} />
      </Field>
      <div>
        <Label>Ta med</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {UTRUSTNING.map(g => <Chip key={g} selected={draft.utrustning.includes(g)} onClick={() => toggleGear(g)}>{g}</Chip>)}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <Field label="Kontaktperson"><Input value={draft.contactName} onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, contactName: v })); }} /></Field>
        <Field label="Telefon"><Input type="tel" value={draft.contactPhone} placeholder="070-123 45 67" onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, contactPhone: v })); }} /></Field>
      </div>

      <Card padding={14}>
        {edit ? (
          <Switch checked={notify} onChange={setNotify} label="Mejla ändringen till deltagarna" />
        ) : (
          <Switch checked={draft.sendEmail} onChange={v => setDraft(d => ({ ...d, sendEmail: v }))} label={`Mejla inbjudan till ${withEmail} ${withEmail === 1 ? 'person' : 'personer'}`} />
        )}
        <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 6 }}>
          {edit ? 'Nya mottagare får alltid sin inbjudan. De som svarat nej mejlas inte.' : 'Alla ser inbjudan i appen och kan svara Ja, Kanske eller Nej.'}
          {withoutEmail > 0 && ` ${withoutEmail} utan e-post får en länk som du kan dela när inbjudan är skickad.`}
        </div>
      </Card>

      <div>
        {tplName === null ? (
          <Button variant="ghost" onClick={() => setTplName(`${draft.title}`)}>Spara såtar och pass som mall</Button>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Input aria-label="Namn på mallen" value={tplName} onChange={e => setTplName(e.target.value)} style={{ flex: '1 1 200px' }} />
            <Button variant="secondary" onClick={async () => {
              try { await api.saveMarkSatTemplate(mark.id, tplName.trim() || draft.title, templateJson(draft)); setTplMsg('Mallen är sparad.'); setTplName(null); } catch (e) { setTplMsg((e as Error).message); }
            }}>Spara mall</Button>
          </div>
        )}
        {tplMsg && <div role="status" style={{ ...TYPE.small, color: T.textSub, marginTop: 6 }}>{tplMsg}</div>}
      </div>
    </div>
  );
}

function StepTitle({ title, sub }: { title: string; sub?: string }) {
  const T = useTheme();
  return (
    <div>
      <h2 style={{ ...TYPE.section, margin: 0 }}>{title}</h2>
      {sub && <p style={{ ...TYPE.body, color: T.textMuted, margin: '4px 0 0' }}>{sub}</p>}
    </div>
  );
}

// ─── Klart ────────────────────────────────────────────────────────

function Done({ ev, edited, draft, mark, onClose }: { ev: MarkCalendarEvent; edited: boolean; draft: PlanDraft; mark: Mark; onClose: () => void }) {
  const T = useTheme();
  const [copied, setCopied] = React.useState<string | null>(null);
  const noEmail = ev.invites.filter(i => !i.email && i.token);
  // Jaktledaren mejlas inte om sin egen jakt.
  const emailed = ev.invites.filter(i => i.email && i.email.toLowerCase() !== (ev.createdBy ?? '').toLowerCase()).length;
  const link = (token: string) => `${window.location.origin}/inbjudan/${token}`;
  const share = async (name: string, token: string) => {
    const url = link(token);
    const text = `${ev.title} ${new Date(ev.startTime).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })} – svara här:`;
    if ('share' in navigator) { try { await navigator.share({ title: ev.title, text, url }); return; } catch { /* avbrutet */ } }
    try { await navigator.clipboard.writeText(`${text} ${url}`); setCopied(name); } catch { window.prompt('Kopiera länken', url); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Inbjudan skickad" style={{ position: 'fixed', inset: 0, zIndex: 3000, background: T.bg, color: T.text, overflowY: 'auto' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '48px 16px 32px' }}>
        <div style={{ width: 56, height: 56, borderRadius: RADIUS.lg, background: T.okSoft, color: T.ok, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon as={Check} size={28} /></div>
        <h2 style={{ ...TYPE.section, margin: '16px 0 4px' }}>{edited ? 'Jakten är uppdaterad' : 'Inbjudan är skickad'}</h2>
        <p style={{ ...TYPE.bodyL, color: T.textSub, margin: 0 }}>
          {ev.title} på {mark.name}. {draft.sendEmail || edited ? `${emailed} ${emailed === 1 ? 'person' : 'personer'} med e-post får den i ett mejl. ` : ''}Svaren syns i Kalender.
        </p>
        {noEmail.length > 0 && (
          <Card padding={0} style={{ marginTop: 20 }}>
            <div style={{ padding: '12px 16px 4px', ...TYPE.small, color: T.textMuted }}>De här saknar e-post. Skicka länken själv, t.ex. i sms:</div>
            {noEmail.map((i, idx) => (
              <ListRow key={i.id} divider={idx < noEmail.length - 1} title={i.name} sub={i.phone ?? undefined}
                trailing={<Button variant="secondary" icon={<Icon as={copied === i.name ? Check : ('share' in navigator ? Share2 : Copy)} size={16} />} onClick={() => share(i.name, i.token!)}>{copied === i.name ? 'Kopierad' : 'Dela länk'}</Button>} />
            ))}
          </Card>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
          <Button onClick={onClose} icon={<Icon as={ArrowLeft} size={18} />}>Till kalendern</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Förhandsvisning ──────────────────────────────────────────────

function previewInvitation(d: PlanDraft, members: PlanMember[], mark: Mark, me: PlanMember | null, userName: string | null, gameRows: HuntGameRow[], fees: Record<string, { fee: number | null; feeFrom: boolean }> = {}): HuntInvitation {
  const rec = recipientsOf(d, members);
  const meKey = me ? `m:${me.id}` : null;
  const hostName = me?.name || userName || 'Jaktledaren';
  const participants = rec.map(r => ({ name: r.name, status: r.key === meKey ? 'yes' : 'invited', passLabel: passLabelFor(d, r.key), isGuest: r.isGuest, isHost: r.key === meKey }));
  if (!meKey) participants.unshift({ name: hostName, status: 'yes', passLabel: null, isGuest: false, isHost: true });
  // Visa förhandsvisningen ur en mottagares perspektiv: den första med pass, annars den första som inte är jaktledaren.
  const sample = rec.find(r => r.key !== meKey && passLabelFor(d, r.key)) ?? rec.find(r => r.key !== meKey);
  return {
    token: null, status: 'invited', comment: null, respondedAt: null,
    name: sample?.name ?? null, passLabel: sample ? passLabelFor(d, sample.key) : null,
    isGuest: !!sample?.isGuest, isHost: false, hostName,
    event: {
      id: 'preview', markId: mark.id, title: d.title || 'Jakt', startTime: toIso(d.date, d.startTime) ?? new Date().toISOString(), endTime: d.endTime ? toIso(d.date, d.endTime) : null,
      status: 'planned', satPlanJson: JSON.stringify({ version: 2, satar: d.satar, utrustning: d.utrustning }),
      meetingTime: toIso(d.date, d.meetingTime), meetingPlace: d.meetingPlace || null, meetingLat: d.meetingLat, meetingLng: d.meetingLng,
      message: d.message || null, contactName: d.contactName || null, contactPhone: d.contactPhone || null, revision: 1, cancelledAt: null,
    },
    mark: { id: mark.id, name: mark.name, imageUrl: mark.imageUrl ?? null, county: mark.county ?? null, lat: mark.mapLat ?? null, lng: mark.mapLng ?? null },
    participants,
    // Fällavgiften räknas per mottagare (lägsta över ens grupper) när inbjudan skickas.
    gameRows: gameRows.map(g => ({ ...g, fee: fees[g.gameType]?.fee ?? null, feeFrom: fees[g.gameType]?.feeFrom ?? false })),
  };
}
